/**
 * React Query hooks for project management
 */

import { useEffect, useRef, useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type { ParticipantRole, Project } from "@/services/project-service"

import { hasUnobservedProjectFileGenerations } from "@/features/projects/utils/project-file-status"
import { queryKeys } from "@/lib/query-keys"
import { ProjectService } from "@/services/project-service"
import { SettingsService } from "@/services/settings-service"
import { useProjectFileUploadRefreshStore } from "@/stores/project-file-upload-refresh-store"

/**
 * Query hook to fetch projects for a session type
 */
export function useProjects(sessionType?: string) {
  return useQuery({
    queryKey: queryKeys.projects(sessionType),
    queryFn: () => ProjectService.listProjects(sessionType),
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchOnWindowFocus: true, // Projects may change, refetch when user returns
  })
}

/**
 * Query hook to fetch a single project by ID
 * Reuses the projects list cache from useProjects to avoid duplicate fetches
 * Uses select to prevent re-renders when other projects change
 */
export function useProject(projectId: string | undefined, sessionType?: string) {
  return useQuery({
    queryKey: queryKeys.projects(sessionType),
    queryFn: () => ProjectService.listProjects(sessionType),
    select: (projects) => projects.find((p) => p.id === projectId),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: true,
  })
}

/**
 * Query hook to fetch project files
 *
 * Polls while any file needs re-indexing (in_vector_store === false but present
 * in blob storage) — the backend eagerly kicks off re-indexing as soon as this
 * state is observed (see routes/projects.py::list_project_files), so refetching
 * here picks up the flip back to in_vector_store === true without any new
 * polling infrastructure, mirroring useProjectEmbeddingStatus below.
 *
 * Bounded to MAX_POLLING_DURATION_MS: the eager trigger only fires for
 * viewers with edit access (readers can't cause it, see #2330 security fix),
 * so a reader-only session would otherwise poll forever with no backend work
 * ever resolving it. An editor visiting should resolve well within the cap;
 * past it we stop polling rather than hammering the backend indefinitely.
 *
 * `canEditOverride` lets a caller that already knows the viewer's role (e.g.
 * from a project object it already fetched) skip this hook's own lookup.
 * When omitted, `canEdit` is derived internally from `useProject` instead of
 * defaulting to `true` -- TanStack Query merges `refetchInterval` across every
 * mounted observer of the same query key (the effective interval is the
 * minimum any observer requests), so if even one caller of this hook for the
 * same project fell back to a hardcoded `true`, a reader would still get
 * polled on that other observer's behalf regardless of what this call site
 * decided. Deriving it here by default closes that gap for every caller that
 * doesn't have a cheaper answer already in hand.
 *
 * The returned `isReindexPolling` flag tells callers whether re-indexing is
 * genuinely still in flight from this viewer's perspective -- `false` for
 * readers, and for editors once the poll cap is hit -- so a UI banner
 * claiming "in progress" isn't shown when nothing actually is. It's backed by
 * a scheduled state flip (not just a ref read at render time): `refetchInterval`
 * stops ticking once the cap is hit, so nothing would otherwise trigger the
 * re-render needed to notice that and the banner could stay stuck showing
 * "in progress" indefinitely.
 */
export function useProjectFiles(projectId: string | undefined, canEditOverride?: boolean) {
  const POLLING_INTERVAL_MS = 5000
  const MAX_POLLING_DURATION_MS = 2 * 60 * 1000
  // Keyed by projectId (not just a bare timestamp) so switching to a
  // different project doesn't inherit a stale/expired start time from the
  // previously-viewed project and wrongly suppress polling for the new one.
  const reindexingStateRef = useRef<{ projectId: string; startedAt: number } | null>(null)
  const [pollCapHit, setPollCapHit] = useState(false)
  const pendingUploadRefresh = useProjectFileUploadRefreshStore((state) =>
    projectId ? state.pendingByProject[projectId] : undefined
  )
  const settleObservedUploadGenerations = useProjectFileUploadRefreshStore(
    (state) => state.settleObserved
  )
  const expireUploadRefresh = useProjectFileUploadRefreshStore((state) => state.expire)
  const forceRefresh = Boolean(pendingUploadRefresh && pendingUploadRefresh.expiresAt > Date.now())

  // No sessionType filter: /api/projects with none returns every project the
  // viewer can see, so this finds the role regardless of which session-type
  // context the caller's page is otherwise scoped to. While the role hasn't
  // resolved yet, default to permissive (true) rather than stalling polling
  // for an editor who just hasn't had their role looked up yet.
  const { data: project } = useProject(projectId)
  const derivedCanEdit = project ? project.currentUserRole !== "reader" : true
  const canEdit = canEditOverride ?? derivedCanEdit

  // A newly registered upload is a new polling generation. Clear any earlier
  // re-index cap so normal vector-store polling gets a fresh bounded window
  // after the upload-observation latch settles.
  useEffect(() => {
    if (!projectId || !pendingUploadRefresh) return
    reindexingStateRef.current = null
    setPollCapHit(false)
  }, [pendingUploadRefresh?.expiresAt, projectId])

  const query = useQuery({
    queryKey: queryKeys.projectFiles(projectId || ""),
    queryFn: () => ProjectService.listFiles(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data || !projectId || !canEdit) return false
      // A successful upload can race process-local project-cache invalidation.
      // Keep the shared query refreshing until its project-scoped owner has
      // observed the exact uploaded generation.
      if (forceRefresh) return POLLING_INTERVAL_MS
      const isReindexing = data.some(
        (f) =>
          f.vector_store_eligible !== false &&
          f.in_vector_store === false &&
          f.needs_reupload !== true
      )
      if (!isReindexing) {
        reindexingStateRef.current = null
        return false
      }
      if (reindexingStateRef.current?.projectId !== projectId) {
        reindexingStateRef.current = { projectId, startedAt: Date.now() }
        setPollCapHit(false)
      }
      const elapsed = Date.now() - reindexingStateRef.current.startedAt
      return elapsed < MAX_POLLING_DURATION_MS ? POLLING_INTERVAL_MS : false
    },
  })

  const hasFilesNotInVectorStore = query.data?.some(
    (f) =>
      f.vector_store_eligible !== false && f.in_vector_store === false && f.needs_reupload !== true
  )
  const isUploadRefreshPending = Boolean(
    forceRefresh &&
    pendingUploadRefresh &&
    hasUnobservedProjectFileGenerations(query.data, pendingUploadRefresh.generations)
  )

  useEffect(() => {
    if (!projectId || !pendingUploadRefresh) return
    if (!hasUnobservedProjectFileGenerations(query.data, pendingUploadRefresh.generations)) {
      settleObservedUploadGenerations(projectId, query.data)
    }
  }, [projectId, pendingUploadRefresh, query.data, settleObservedUploadGenerations])

  useEffect(() => {
    if (!projectId || !pendingUploadRefresh) return
    const remaining = pendingUploadRefresh.expiresAt - Date.now()
    if (remaining <= 0) {
      expireUploadRefresh(projectId, pendingUploadRefresh.expiresAt)
      return
    }
    const timer = setTimeout(
      () => expireUploadRefresh(projectId, pendingUploadRefresh.expiresAt),
      remaining
    )
    return () => clearTimeout(timer)
  }, [expireUploadRefresh, pendingUploadRefresh, projectId])

  // Schedules the exact state flip that marks the poll cap as hit, so
  // isReindexPolling below re-renders true -> false right when polling
  // actually stops instead of only whenever some unrelated render happens by.
  useEffect(() => {
    if (!canEdit || !hasFilesNotInVectorStore || !projectId) return
    const state = reindexingStateRef.current
    if (!state || state.projectId !== projectId) return
    const remaining = state.startedAt + MAX_POLLING_DURATION_MS - Date.now()
    if (remaining <= 0) {
      setPollCapHit(true)
      return
    }
    const timer = setTimeout(() => setPollCapHit(true), remaining)
    return () => clearTimeout(timer)
  }, [canEdit, hasFilesNotInVectorStore, projectId, query.dataUpdatedAt])

  const isReindexPolling = canEdit && !!hasFilesNotInVectorStore && !pollCapHit

  return { ...query, isReindexPolling, isUploadRefreshPending }
}

/**
 * Query hook to poll embedding status for project files
 * Automatically stops polling when all files are embedded
 */
export function useProjectEmbeddingStatus(projectId: string | undefined, enabled = true) {
  const POLLING_INTERVAL_MS = 5000

  return useQuery({
    queryKey: queryKeys.projectEmbeddingStatus(projectId || ""),
    queryFn: () => ProjectService.getEmbeddingStatus(projectId!),
    enabled: !!projectId && enabled,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    gcTime: 1000 * 60, // Keep in cache for 1 minute
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return POLLING_INTERVAL_MS
      // Stop polling when complete
      return data.is_complete ? false : POLLING_INTERVAL_MS
    },
  })
}

/**
 * Mutation hook for creating a project.
 * Error feedback is left to the caller (ProjectsDialog shows the localized
 * projects.create_error toast) — no hook-level error toast to avoid doubles.
 */
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { project_id?: string; name?: string; session_type: string }) =>
      ProjectService.createProject(payload),
    onSuccess: (newProject, variables) => {
      // Invalidate projects list for the session type
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projects(variables.session_type),
      })
      toast.success("Project created successfully")
    },
  })
}

/**
 * Mutation hook for updating a project
 */
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string
      data: Partial<
        Pick<
          Project,
          | "name"
          | "instructions"
          | "defaultWebSearch"
          | "defaultModelId"
          | "defaultEnabledIndexIds"
          | "trustedSourceUrls"
        >
      >
    }) => ProjectService.updateProject(projectId, data),
    onSuccess: (updatedProject) => {
      // Invalidate projects list (will refetch with updated data)
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() })
      // Also invalidate specific project if we had a query key for it
      void queryClient.invalidateQueries({ queryKey: queryKeys.project(updatedProject.id) })
      toast.success("Project updated successfully")
    },
    onError: (error: Error) => {
      toast.error("Failed to update project", {
        description: error.message || "An error occurred",
      })
    },
  })
}

/**
 * Mutation hook for deleting a project
 */
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (projectId: string) => ProjectService.deleteProject(projectId),
    onSuccess: () => {
      // Invalidate projects list
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() })
      toast.success("Project deleted successfully")
    },
    onError: (error: Error) => {
      toast.error("Failed to delete project", {
        description: error.message || "An error occurred",
      })
    },
  })
}

/**
 * Mutation hook for uploading files to a project
 */
export function useUploadProjectFiles() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, files }: { projectId: string; files: File[] }) =>
      ProjectService.uploadFiles(projectId, files),
    onSuccess: (result, variables) => {
      // Register against the mutation's captured project ID. Every mounted
      // useProjectFiles observer for that project now owns the refresh, so a
      // dialog unmount or route change cannot lose it.
      useProjectFileUploadRefreshStore.getState().register(variables.projectId, result.files)
      // Invalidate project files list and embedding status so UI reflects new pending files
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectFiles(variables.projectId),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectEmbeddingStatus(variables.projectId),
      })
      toast.success("Files uploaded successfully")
    },
    onError: (error: Error) => {
      toast.error("Failed to upload files", {
        description: error.message || "An error occurred",
      })
    },
  })
}

/**
 * Mutation hook for deleting a file from a project
 */
export function useDeleteProjectFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, filename }: { projectId: string; filename: string }) =>
      ProjectService.deleteFile(projectId, filename),
    onSuccess: (_, variables) => {
      // Invalidate project files list
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectFiles(variables.projectId),
      })
      toast.success("File deleted successfully")
    },
    onError: (error: Error) => {
      toast.error("Failed to delete file", {
        description: error.message || "An error occurred",
      })
    },
  })
}

/**
 * Mutation hook for marking a project's invitation notification as viewed.
 * Shares the unviewed state with the per-item dot and the Updates strip, so
 * the projects list is invalidated to refresh both.
 */
export function useMarkProjectViewed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (projectId: string) => ProjectService.markProjectViewed(projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() })
    },
  })
}

// ==================== Participant Hooks ====================

/**
 * Query hook to fetch project participants
 */
export function useProjectParticipants(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projectParticipants(projectId || ""),
    queryFn: () => ProjectService.listParticipants(projectId!),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })
}

/**
 * Mutation hook for adding a participant to a project
 */
export function useAddParticipant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      participant,
    }: {
      projectId: string
      participant: {
        oid: string
        displayName: string
        type: "user" | "group"
        role: ParticipantRole
      }
    }) => ProjectService.addParticipant(projectId, participant),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectParticipants(variables.projectId),
      })
      // Also invalidate projects list as participants affect access
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() })
      toast.success("Participant added successfully")
    },
    onError: (error: Error) => {
      toast.error("Failed to add participant", {
        description: error.message || "An error occurred",
      })
    },
  })
}

/**
 * Mutation hook for updating a participant's role
 */
export function useUpdateParticipantRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      projectId,
      participantOid,
      role,
    }: {
      projectId: string
      participantOid: string
      role: ParticipantRole
    }) => ProjectService.updateParticipantRole(projectId, participantOid, role),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectParticipants(variables.projectId),
      })
      toast.success("Role updated successfully")
    },
    onError: (error: Error) => {
      toast.error("Failed to update role", {
        description: error.message || "An error occurred",
      })
    },
  })
}

/**
 * Mutation hook for removing a participant from a project
 */
export function useRemoveParticipant() {
  const queryClient = useQueryClient()
  const currentUserOid = SettingsService.getInstance().claims.oid

  return useMutation({
    mutationFn: ({ projectId, participantOid }: { projectId: string; participantOid: string }) =>
      ProjectService.removeParticipant(projectId, participantOid),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectParticipants(variables.projectId),
      })
      // Also invalidate projects list as participants affect access
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() })
      const isSelfLeave = variables.participantOid === currentUserOid
      if (isSelfLeave) {
        toast.success("You have left the project")
      } else {
        toast.success("Participant removed successfully")
      }
    },
    onError: (error: Error) => {
      toast.error("Failed to remove participant", {
        description: error.message || "An error occurred",
      })
    },
  })
}

/**
 * Mutation hook for transferring project ownership
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, newOwnerOid }: { projectId: string; newOwnerOid: string }) =>
      ProjectService.transferOwnership(projectId, newOwnerOid),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectParticipants(variables.projectId),
      })
      // Invalidate projects list as ownership and roles change
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects() })
      toast.success("Ownership transferred successfully", {
        description: "You are now an admin of this project.",
      })
    },
    onError: (error: Error) => {
      toast.error("Failed to transfer ownership", {
        description: error.message || "An error occurred",
      })
    },
  })
}
